import type { Middleware } from '@reduxjs/toolkit';

/**
 * Action-type fragments that should not be logged by the performance
 * middleware. RTK Query's internal lifecycle actions (executeQuery,
 * executeMutation, and subscription bookkeeping) fire many times per
 * request and pollute the console on every page load without providing
 * useful signal. Suppressing them matches FRE-622's "silence per-request
 * subscription churn" remediation.
 */
const NOISY_ACTION_FRAGMENTS: readonly string[] = [
  '/executeQuery/',
  '/executeMutation/',
  'internal_getRTKQSubscriptions',
  '/subscriptionsUpdated',
  '/unsubscribeQueryResult',
  '/middlewareRegistered',
];

function isNoisyAction(actionType: string): boolean {
  return NOISY_ACTION_FRAGMENTS.some((fragment) => actionType.includes(fragment));
}

export const performanceLoggerMiddleware: Middleware = () => (next) => (action) => {
  if (process.env.NODE_ENV === 'production') {
    return next(action);
  }

  const start =
    typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  const result = next(action);
  const end =
    typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();

  // Structural narrowing instead of an `AnyAction` cast keeps strict
  // TypeScript happy and guards against actions whose `type` isn't a string
  // (e.g. thunks dispatched as plain functions).
  const actionType =
    typeof action === 'object' &&
    action !== null &&
    'type' in action &&
    typeof action.type === 'string'
      ? action.type
      : 'unknown';
  if (!isNoisyAction(actionType)) {
    console.debug('[Redux]', actionType, 'in', Math.round(end - start), 'ms');
  }

  return result;
};
